import { useState } from "react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Contact, Owner, UserHistory } from "@/types";
import { Clock, Check } from "lucide-react";

interface ContactsTabsCardProps {
  owner: Owner;
  additionalContacts?: Contact[];
  className?: string;
  userHistory?: UserHistory;
  emailVerifiedAt?: string | null;
}

export function ContactsTabsCard({ owner, additionalContacts = [], className, userHistory, emailVerifiedAt }: ContactsTabsCardProps) {
  const [activeTab, setActiveTab] = useState("owner");

  return (
    <Card className={className}>
      <CardHeader className="pb-2 flex flex-row items-start justify-between">
        <CardTitle className="text-base">Contact Information</CardTitle>
        {userHistory && (
          <div className="flex items-center text-xs text-muted-foreground">
            <Clock className="w-3 h-3 mr-1" />
            {userHistory.created && (
              <span>Created by: {userHistory.created.name}</span>
            )}
          </div>
        )}
      </CardHeader>
      <CardContent className="pt-0">
        <Tabs defaultValue="owner" value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="flex flex-wrap gap-2 mb-4 bg-transparent p-0 h-auto">
            <TabsTrigger value="owner" className="text-xs py-2 px-3 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">Owner</TabsTrigger>
            {additionalContacts && additionalContacts.length > 0 && (
              <>
                {additionalContacts.map((_, index) => (
                  <TabsTrigger key={`contact-tab-${index}`} value={`contact-${index}`} className="text-xs py-2 px-3 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
                    Contact {index + 1}
                  </TabsTrigger>
                ))}
              </>
            )}
          </TabsList>

          <TabsContent value="owner" className="mt-0">
            <div className="bg-card rounded-md p-2 border">
              <div className="grid grid-cols-2 gap-1 text-sm">
                <span className="font-medium">Title:</span>
                <span>{owner.title || "N/A"}</span>
                <span className="font-medium">Name:</span>
                <span>{owner.firstName || "N/A"}</span>
                {owner.middleName && (
                  <>
                    <span className="font-medium">Middle Name:</span>
                    <span>{owner.middleName}</span>
                  </>
                )}
                <span className="font-medium">Surname:</span>
                <span>{owner.lastName || "N/A"}</span>
                <span className="font-medium">Email:</span>
                <span className="flex items-center">
                  {owner.email || "N/A"}
                  {emailVerifiedAt && <Check className="ml-2 h-4 w-4 text-green-500" />}
                </span>
                {owner.phone && (
                  <>
                    <span className="font-medium">Phone:</span>
                    <span>{owner.phone}</span>
                  </>
                )}
                {userHistory && (
                  <>
                    <span className="font-medium">Created by:</span>
                    <span>{userHistory.created?.name || "N/A"} ({userHistory.created?.role || "N/A"})</span>
                    {userHistory.updated && (
                      <>
                        <span className="font-medium">Last updated by:</span>
                        <span>{userHistory.updated.name || "N/A"} ({userHistory.updated.role || "N/A"})</span>
                      </>
                    )}
                  </>
                )}
              </div>
            </div>
          </TabsContent>

          {additionalContacts && additionalContacts.map((contact, index) => (
            <TabsContent key={`contact-content-${index}`} value={`contact-${index}`} className="mt-0">
              <div className="bg-card rounded-md p-2 border">
                <div className="grid grid-cols-2 gap-1 text-sm">
                  <span className="font-medium">Title:</span>
                  <span>{contact.title || "N/A"}</span>
                  <span className="font-medium">Name:</span>
                  <span>{contact.firstName || "N/A"}</span>
                  {contact.middleName && (
                    <>
                      <span className="font-medium">Middle Name:</span>
                      <span>{contact.middleName}</span>
                    </>
                  )}
                  <span className="font-medium">Surname:</span>
                  <span>{contact.lastName || "N/A"}</span>
                  <span className="font-medium">Email:</span>
                  <span>{contact.email || "N/A"}</span>
                </div>
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </CardContent>
    </Card>
  );
}